import { BORDERS, COLORS, SHADOWS } from '@alto/design-library-tokens';
import React, { forwardRef } from 'react';
import { type ImageSourcePropType, type StyleProp, View, type ViewStyle } from 'react-native';
import styled from 'styled-components/native';
import { type StandaloneCheckbox, type Switch } from '../../buttons';
import { Column } from '../../containers';
import { type AltoIcon } from '../../icon';
import { type Tag } from '../../tags';
import { type Link, type Text } from '../../typography';
import { CardHeader } from './CardHeader';

export type CardRightContentProps = React.ReactElement<
  typeof StandaloneCheckbox | typeof Switch | typeof Text | typeof Link | typeof AltoIcon
>;

export type CardProps = {
  readonly children: React.ReactNode;
  readonly flexGrow?: number;
  readonly title?: string;
  readonly overflow?: 'hidden' | 'visible' | 'scroll';
  readonly description?: string;
  readonly style?: StyleProp<ViewStyle>;
  readonly RightContent?: CardRightContentProps;
  readonly illustrationSrc?: ImageSourcePropType;
  readonly illustrationAccessibilityLabel?: string | null;
  readonly tag?: React.ReactElement<typeof Tag>;
  readonly wrapHeader?: boolean;
};

const StyledCard = styled(View)<{ flexGrow: CardProps['flexGrow']; overflow?: 'hidden' | 'visible' | 'scroll' }>`
  ${({ flexGrow }) => (flexGrow ? `flex-grow: ${flexGrow};` : ``)}
  flex-shrink: 1;
  ${({ overflow }) => (overflow ? `overflow: ${overflow};` : ``)}
  background-color: ${() => COLORS.PALETTE.GREYSCALE.WHITE};
  border-width: 1px;
  border-style: solid;
  border-color: ${() => COLORS.BORDER_COLORS.LIGHT};
  border-radius: ${BORDERS.RADIUS.LG.px};
  ${SHADOWS.BOTTOM_LIGHT}
`;

const CardWrapper = styled(View)`
  position: relative;
  overflow: visible;
`;

// Clip full-bleed hover rows to the card's outer rounded corners. When a CardHeader sits above,
// only bottom corners need rounding — top-of-wrapper is mid-card (square). Untitled cards round all four.
const ContentWrapper = styled(Column)<{ hasHeader: boolean }>`
  overflow: hidden;
  border-bottom-left-radius: ${BORDERS.RADIUS.LG.px};
  border-bottom-right-radius: ${BORDERS.RADIUS.LG.px};
  ${({ hasHeader }) =>
    !hasHeader &&
    `border-top-left-radius: ${BORDERS.RADIUS.LG.px};
     border-top-right-radius: ${BORDERS.RADIUS.LG.px};`}
`;

export const Card = forwardRef<View, CardProps>(
  (
    {
      children,
      flexGrow,
      title,
      description,
      illustrationSrc,
      illustrationAccessibilityLabel,
      RightContent,
      tag,
      style,
      overflow,
      wrapHeader,
    },
    ref,
  ) => {
    const hasHeader = !!title;

    return (
      <StyledCard
        style={style}
        flexGrow={flexGrow}
        overflow={overflow}
        ref={ref}
      >
        {hasHeader && (
          <CardWrapper>
            <CardHeader
              title={title}
              RightContent={RightContent}
              illustrationSrc={illustrationSrc}
              illustrationAccessibilityLabel={illustrationAccessibilityLabel}
              tag={tag}
              description={description}
              wrapHeader={wrapHeader}
            />
          </CardWrapper>
        )}
        <ContentWrapper
          hasHeader={hasHeader}
          flexGrow={1}
          testID="card-content-wrapper"
        >
          {children}
        </ContentWrapper>
      </StyledCard>
    );
  },
);

Card.displayName = 'Card';
